import { OverrideProposalEventResponse } from "@/Common//Api/PopulationBasedFunding/Dto/OverrideProposalEventsResponse";
import { AsyncAction } from "@/Common/Api/AsyncActions/AsyncAction";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { ExternalBillingUserInfoDto } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingUserInfoDto";
import { AcceptInternalPopulationBasedFundingProposalResponse } from "@/Common/Api/PopulationBasedFunding/Dto/AcceptInternalPopulationBasedFundingProposalResponse";
import { AcceptPopulationBasedFundingEventsResponse } from "@/Common/Api/PopulationBasedFunding/Dto/AcceptPopulationBasedFundingEventsResponse";
import { DeregisterPatientResponse } from "@/Common/Api/PopulationBasedFunding/Dto/DeregisterPatientResponse";
import PatientHistoryResponse from "@/Common/Api/PopulationBasedFunding/Dto/PatientHistoryResponse";
import { RegisterPatientResponse } from "@/Common/Api/PopulationBasedFunding/Dto/RegisterPatientResponse";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PatientPopulationBasedFundingModel } from "@/Common/Models/PopulationBasedFunding/PatientPopulationBasedFundingModel";
import { PopulationBasedFundingEventWithNotesModel } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingEventWithNotesModel";
import { PopulationBasedFundingFormData } from "@/Common/Models/PopulationBasedFunding/PopulationBasedFundingFormData";
import { AdministrativeCode, PopulationBasedFundingEventDeregisteredReason } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { Server } from "@/Common/Operations/operations.server";
import * as ServerOperations from "@/Common/Operations/operations.server";

export class PopulationBasedFundingActions extends AsyncAction {
    private static actions = new PopulationBasedFundingActions();

    public static async registerPatient(
        patientModel: Patient,
        formData: PopulationBasedFundingFormData
    ): Promise<RegisterPatientResponse> {
        return this.actions.postAsync<RegisterPatientResponse>(
            ServerOperations.Server.Myle_PopulationBasedFunding_RegisterPatient,
            {
                PatientContactFacetId: patientModel.PatientResponse.ContactFacet_id,
                SelectedPhysicianContactFacetId: formData.SelectedPhysician.ContactFacetId,
                Notes: formData.Notes,
                EffectiveDate: formData.SelectedEffectiveDate,
                AdministrativeCode: formData.AdministrativeCode
            }
        );
    }

    public static async acceptInternalRegistrationProposalAndUpdateHistory(
        eventId: string,
        formData: PopulationBasedFundingFormData,
        patientModel: Patient
    ): Promise<AcceptInternalPopulationBasedFundingProposalResponse> {
        const response = await this.actions.postAsync<AcceptInternalPopulationBasedFundingProposalResponse>(
            ServerOperations.Server.Myle_PopulationBasedFunding_AcceptInternalRegistrationProposal,
            {
                EventId: eventId.toString(),
                EmployeeContactFacetId: formData.SelectedPhysician.ContactFacetId,
                EffectiveDate: formData.SelectedEffectiveDate,
                Notes: formData.Notes,
                AdministrativeCode: formData.AdministrativeCode
            }
        );

        if (!response?.IsSuccess) return;

        const acceptedInternalProposal = new PopulationBasedFundingEventWithNotesModel(
            {
                ...patientModel.PopulationBasedFunding.history.firstOrDefault(event => event.Id === eventId),
                AcceptedBy: response.AcceptedBy,
                AcceptedDate: response.AcceptedDate,
                EmployeeContactFacetId: formData.SelectedPhysician.ContactFacetId,
                AdministrativeCode: formData.AdministrativeCode
            }
        );

        const nextHistory = patientModel.PopulationBasedFunding.history.filter(event => event.Id !== eventId);
        nextHistory.push(acceptedInternalProposal);

        this.dispatchLocal({ history: nextHistory }, patientModel);

        return response;
    }

    public static async deregisterPatient(
        patientContactFacetId: string,
        formData: PopulationBasedFundingFormData
    ): Promise<DeregisterPatientResponse> {
        return this.actions.postAsync<DeregisterPatientResponse>(
            ServerOperations.Server.Myle_PopulationBasedFunding_DeregisterPatient,
            {
                PatientContactFacetId: patientContactFacetId,
                SelectedPhysicianContactFacetId: formData.SelectedPhysician.ContactFacetId,
                Notes: formData.Notes,
                EffectiveDate: formData.SelectedEffectiveDate,
                Reason: formData.SelectedDeregisterReason,
                DeregistrationOrProposalCustomReason: formData.DeregistrationOrProposalCustomReason,
                WillContinueToSeePhysician: formData.WillContinueToSeePhysician
            }
        );
    }

    public static async getLatestActiveEvent(patientContactFacetId: string): Promise<PopulationBasedFundingEventWithNotesModel> {
        const response = await this.actions.postAsync<{ LatestActiveEvent: PopulationBasedFundingEventWithNotesModel, Errors: ErrorMessage[] }>(
            ServerOperations.Server.Myle_PopulationBasedFundingEvent_GetLatestActiveEvent,
            { PatientContactFacetId: patientContactFacetId }
        );

        if (response.Errors.any())
            console.error(response.Errors);

        return new PopulationBasedFundingEventWithNotesModel(response.LatestActiveEvent);
    }

    public static async deleteEvent(eventId: string): Promise<GenericResponse> {
        return this.actions.postAsync<GenericResponse>(
            Server.Myle_PopulationBasedFundingEvent_Delete,
            { eventId: eventId }
        );
    }

    public static async loadPatientHistory(patientModel: Patient): Promise<void> {
        const response: PatientHistoryResponse = await this.actions.postAsync<PatientHistoryResponse>(
            Server.Myle_PopulationBasedFundingEvent_GetPatientHistory,
            { patientContactFacetId: patientModel.PatientResponse.ContactFacet_id }
        );

        this.dispatchLocal({ history: response.PopulationBasedFundingEvents }, patientModel);
    }

    public static async editEvent(
        eventId: string,
        selectedPhysicianContactFacetId: string,
        selectedEffectiveDate: Date,
        note: string,
        administrativeCode: AdministrativeCode,
        selectedDeregisterReason?: PopulationBasedFundingEventDeregisteredReason,
        deregistrationOrProposalCustomReason?: string,
        willContinueTreatment?: boolean
    ): Promise<GenericResponse> {
        return this.actions.postAsync<GenericResponse>(
            Server.Myle_PopulationBasedFundingEvent_Edit,
            {
                EventId: eventId,
                EmployeeContactFacetId: selectedPhysicianContactFacetId,
                EffectiveDate: selectedEffectiveDate,
                Notes: note,
                DeregisterReason: selectedDeregisterReason,
                DeregistrationOrProposalCustomReason: deregistrationOrProposalCustomReason,
                WillContinueTreatment: willContinueTreatment,
                AdministrativeCode: administrativeCode
            }
        );
    }

    public static expandCollapseAllEvents(patientModel: Patient): void {
        const areAllItemsCollapsed = (patientModel.PopulationBasedFunding.extendedEventIds?.length ?? 0) === 0;

        const nextExtendedItems = areAllItemsCollapsed
            ? patientModel.PopulationBasedFunding.history?.map(item => item.Id) ?? []
            : [];

        this.dispatchLocal({ extendedEventIds: nextExtendedItems }, patientModel);
    }

    public static expandCollapseOne(event: PopulationBasedFundingEventWithNotesModel, patientModel: Patient): void {
        const extendedItems = patientModel.PopulationBasedFunding.extendedEventIds;

        const nextExtendedItems = extendedItems?.contains(event.Id)
            ? extendedItems.filter(id => id !== event.Id)
            : [...(extendedItems ?? []), event.Id];

        this.dispatchLocal({ extendedEventIds: nextExtendedItems }, patientModel);
    }

    public static async getLatestRegistration(patientContactFacetId: string): Promise<PopulationBasedFundingEventWithNotesModel> {
        const response = await this.actions.postAsync<{ LatestRegistrationEvent: PopulationBasedFundingEventWithNotesModel, Errors: ErrorMessage[] }>(
            ServerOperations.Server.Myle_PopulationBasedFunding_GetLatestRegistration,
            {
                PatientContactFacetId: patientContactFacetId
            }
        );

        return response.LatestRegistrationEvent;
    }

    public static updateIcon(patientContactFacetId: string): void {
        sessionStore.dispatch(
            LocalPatientOperations.Profile.PopulationBasedFunding_SignalIconUpdate,
            {
                ...patientStore.PopulationBasedFundingUpdateIconSignal,
                patientContactFacetId
            }
        );
    }

    public static deleteEventFromExtendedItems(eventId: string, patientModel: Patient): void {
        const extendedItems = patientModel.PopulationBasedFunding.extendedEventIds;

        const nextExtendedItems = extendedItems?.filter(id => id !== eventId);

        this.dispatchLocal({ extendedEventIds: nextExtendedItems }, patientModel);
    }

    public static async showBillingLoginfNotLoggedInAsync(
        onExternalBillingAuth: () => void
    ): Promise<boolean> {
        const billingUser: ExternalBillingUserInfoDto | null = await sessionStore.getCurrentEmployee().getExternalBillingUserAsync();

        if (!billingUser?.BillingAuthenticationIsValid) {
            sessionStore.dispatchMain(
                LocalOperations.MainComponentViewOperations.DisplayExternalBillingAuthentication,
                {
                    billingProvider: sessionStore.getCurrentEmployee().getBillingProvider(),
                    onExternalBillingAuth: onExternalBillingAuth
                }
            );
            return true;
        }

        return false;
    }

    public static async loadPatientHistoryAndExpandAll(patientModel: Patient): Promise<void> {
        const response: PatientHistoryResponse = await this.actions.postAsync<PatientHistoryResponse>(
            Server.Myle_PopulationBasedFundingEvent_GetPatientHistory,
            { patientContactFacetId: patientModel.PatientResponse.ContactFacet_id }
        );

        const nextExtendedItems = response.PopulationBasedFundingEvents?.map(item => item.Id) ?? [];

        this.dispatchLocal({ history: response.PopulationBasedFundingEvents, extendedEventIds: nextExtendedItems }, patientModel);
    }

    public static pushEventToHistoryAndExtendItem(event: PopulationBasedFundingEventWithNotesModel, patientModel: Patient): void {
        const patientHistory = patientModel.PopulationBasedFunding?.history;
        const extendedItems = patientModel.PopulationBasedFunding?.extendedEventIds;

        if (patientHistory?.any()) {
            patientHistory[0].IsLatestInPatientHistory = false;
        }

        this.dispatchLocal({
            history: [...(patientHistory ?? []), event],
            extendedEventIds: [...(extendedItems ?? []), event.Id]
        }, patientModel);
    }

    public static removeEventFromHistoryAndExtendedEventsList(eventToDeleteId: string, patientModel: Patient): void {
        const nextPatientHistory = patientModel.PopulationBasedFunding.history
            .filter(event => event.Id !== eventToDeleteId);
        const nextExtendedItems = patientModel.PopulationBasedFunding.extendedEventIds
            .filter(id => id !== eventToDeleteId);

        if (nextPatientHistory.any()) {
            nextPatientHistory[0].IsLatestInPatientHistory = true;
        }

        this.dispatchLocal({
            history: nextPatientHistory,
            extendedEventIds: nextExtendedItems
        }, patientModel);
    }

    public static async getPatientHasRegistrationHistoryAsync(patientContactFacetId: string): Promise<boolean> {
        const response = await this.actions.postAsync<{ PatientHasRegistrationHistory: boolean }>(
            Server.Myle_PopulationBasedFunding_PatientHasRegistrationHistory,
            { patientContactFacetId }
        );

        return response.PatientHasRegistrationHistory;
    }

    public static async createInternalRegistrationProposal(patientContactFacetId: string): Promise<boolean> {
        const response = await this.actions.postAsync<GenericResponse>(
            Server.Myle_PopulationBasedFunding_CreateInternalRegistrationProposal,
            { patientContactFacetId }
        );

        return response?.IsSuccess ?? false;
    }

    public static async deleteInternalRegistrationProposal(patientContactFacetId: string): Promise<boolean> {
        const response = await this.actions.postAsync<GenericResponse>(
            Server.Myle_PopulationBasedFunding_DeleteInternalRegistrationProposal,
            { patientContactFacetId }
        );

        return response?.IsSuccess ?? false;
    }

    public static async deleteInternalRegistrationProposalFromPatientHistory(patientModel: Patient, eventId: string): Promise<void> {
        const nextHistory = patientModel.PopulationBasedFunding.history.filter(event => event.Id !== eventId);
        this.dispatchLocal({ history: nextHistory }, patientModel);
    }

    public static async acceptProposal(eventId: string, patientModel: Patient): Promise<void> {
        const response = await this.actions.postAsync<AcceptPopulationBasedFundingEventsResponse>(
            Server.Myle_PopulationBasedFunding_AcceptProposals,
            {
                eventIds: [eventId]
            }
        );

        if (!response.IsSuccess) return;

        const acceptedEvent = new PopulationBasedFundingEventWithNotesModel(
            {
                ...patientModel.PopulationBasedFunding.history.firstOrDefault(event => event.Id === eventId),
                AcceptedDate: response.AcceptedDate,
                AcceptedBy: response.AcceptedBy
            }
        );

        const nextHistory = patientModel.PopulationBasedFunding.history.filter(event => event.Id !== eventId);
        nextHistory.push(acceptedEvent);

        this.dispatchLocal({ history: nextHistory }, patientModel);
    }

    public static async overrideRegistrationProposal(
        selectedReasonId: number,
        customReason: string,
        note: string,
        eventId: string,
        patientModel: Patient
    ): Promise<OverrideProposalEventResponse> {
        const response = await this.actions.postAsync<OverrideProposalEventResponse>(
            Server.Myle_PopulationBasedFunding_OverrideRegistrationProposal,
            {
                OverrideReason: selectedReasonId,
                CustomReason: customReason,
                Note: note,
                EventId: eventId
            }
        );

        return this.afterSaveOverride(response, patientModel, eventId);
    }

    public static async overrideDeregistrationProposal(
        selectedReasonId: number,
        customReason: string,
        note: string,
        eventId: string,
        patientModel: Patient
    ): Promise<OverrideProposalEventResponse> {
        const response = await this.actions.postAsync<OverrideProposalEventResponse>(
            Server.Myle_PopulationBasedFunding_OverrideDeregistrationProposal,
            {
                OverrideReason: selectedReasonId,
                CustomReason: customReason,
                Note: note,
                EventId: eventId
            }
        );

        return this.afterSaveOverride(response, patientModel, eventId);
    }

    private static dispatchLocal(changes: Partial<PatientPopulationBasedFundingModel>, patientModel: Patient): void {
        const nextPbfModel = new PatientPopulationBasedFundingModel({ ...patientModel.PopulationBasedFunding, ...changes });

        sessionStore.dispatch(
            LocalPatientOperations.Profile.PopulationBasedFunding_SetPatientData,
            {
                nextPbfModel,
                contactId: patientModel.IndividualResponse.Contact_id
            }
        );
    }

    private static afterSaveOverride(response: OverrideProposalEventResponse, patientModel: Patient, eventId: string): OverrideProposalEventResponse {
        if (!response?.IsSuccess) return null;

        const overrideEvent = new PopulationBasedFundingEventWithNotesModel(
            {
                ...patientModel.PopulationBasedFunding.history.firstOrDefault(event => event.Id === eventId),
                OverrideDate: response.OverrideDate,
                OverrideReasonEnum: response.OverrideReasonEnum,
                DeregistrationOrProposalCustomReason: response.DeregistrationOrProposalCustomReason
            }
        );

        if (response.Note)
            overrideEvent.Notes.push(response.Note);

        const nextHistory = patientModel.PopulationBasedFunding.history.filter(event => event.Id !== eventId);
        nextHistory.push(overrideEvent);

        this.dispatchLocal({ history: nextHistory }, patientModel);

        return response;
    }
}
